import { useCallback } from 'react';
import { useSelector, useStore } from 'react-redux';

import { persistor } from '@/states/persistor';
import {
  glossaryEntriesSlice,
  GlossaryRow,
  GlossaryEntriesState,
} from '@/states/slices/glossaryEntriesSlice';
import {
  AppDispatch,
  RootState,
  getWriteFailureCount,
  getLastWriteFailureErrorName,
  useAppDispatch,
} from '@/states/store';

/**
 * localStorageへの書き込み完了(persistor.flush())を待つ際の上限時間(ミリ秒)。
 *
 * localStorage.setItem 自体は同期APIで完了しない(ハングする)ことは基本的に無いが、
 * 万が一を想定したフェイルセーフとして上限を設ける。これが無いと、呼び出し元
 * （用語集アップロードダイアログ）はスピナー表示のまま操作不能になってしまう
 * (PRISM-3110)。
 */
const FLUSH_TIMEOUT_MS = 10000;

/**
 * persistor.flush() の完了を、指定ミリ秒を上限に待つ。
 *
 * @param timeoutMs 待つ上限時間(ミリ秒)
 * @returns 上限に達して待つのをやめた場合はtrue、上限内に完了した場合はfalse
 */
const flushWithTimeout = async (timeoutMs: number): Promise<boolean> => {
  let timedOut = false;
  let timeoutId: ReturnType<typeof setTimeout> | undefined;

  try {
    const timeoutPromise = new Promise<void>((resolve) => {
      timeoutId = setTimeout(() => {
        timedOut = true;
        resolve();
      }, timeoutMs);
    });

    await Promise.race([persistor.flush(), timeoutPromise]);
  } finally {
    clearTimeout(timeoutId);
  }

  return timedOut;
};

/**
 * 用語集の保存結果。
 * - succeeded: localStorageへの書き込みに成功した
 * - failedQuota: 書き込みに失敗した(容量超過。`QuotaExceededError`)。
 *   登録件数を減らせば解決する可能性がある
 * - failedBlocked: 書き込みに失敗した(容量超過以外。`SecurityError`等)。
 *   プライベートブラウジングやブラウザ設定が原因のため、件数を減らしても解決しない
 * - timeout: 既定時間内に完了/失敗を確認できなかった。バックグラウンドの処理が
 *   その後失敗しても、その失敗はユーザーには通知されない(PRISM-3110のスコープ外)
 */
export const GLOSSARY_SAVE_RESULT = {
  SUCCEEDED: 'succeeded',
  FAILED_QUOTA: 'failedQuota',
  FAILED_BLOCKED: 'failedBlocked',
  TIMEOUT: 'timeout',
} as const;
export type GlossarySaveResult =
  (typeof GLOSSARY_SAVE_RESULT)[keyof typeof GLOSSARY_SAVE_RESULT];

/**
 * 用語集エントリを Redux で管理するカスタムフック。
 *
 * 永続化は redux-persist が担うため、このフックは直接 localStorage に触らない。
 *
 * ### localStorage の容量超過について（PRISM-3110で対応）
 * localStorage の容量を超えた場合、redux-persist は書き込み失敗をデフォルトでは
 * 握りつぶす。`setGlossaryEntries` は書き込み完了を待ってから結果
 * ({@link GlossarySaveResult})を返すため、呼び出し元（用語集アップロードダイアログ）は
 * 失敗時にダイアログを閉じずに残し、具体的な案内をインラインで表示できる。
 */
export const useGlossaryEntries = () => {
  const dispatch: AppDispatch = useAppDispatch();
  const reduxStore = useStore<RootState>();
  const { setGlossaryEntries, clearGlossaryEntries } =
    glossaryEntriesSlice.actions;

  const { entries } = useSelector<RootState, GlossaryEntriesState>(
    (state) => state.glossaryEntries,
  );

  /**
   * 用語集を全件置き換え、localStorageへの書き込み完了を待って結果を返す。
   *
   * 書き込み完了(`persistor.flush()`)には {@link FLUSH_TIMEOUT_MS} の上限を設ける。
   * `localStorage.setItem`は同期APIのため通常はハングしないが、万が一に備えた
   * フェイルセーフであり、呼び出し元(ダイアログ)を操作不能なまま固まらせない
   * ためのもの。上限に達した場合は`timeout`を返す。巻き戻し(後述)の書き込みを
   * 待つ時間も含め、この関数全体で上限を超えて待たない。
   *
   * redux-persist はReduxの状態を書き出す仕組みのため、保存に失敗してもRedux側には
   * 新しいエントリが残ってしまう。そのままだと、エラー表示後にダイアログを閉じると
   * 保存されていないエントリが一覧に表示・利用され続け、リロードすると古い内容に
   * 戻ってしまう。そのため、結果が`succeeded`以外のときは登録前のエントリへ巻き戻し、
   * 画面の内容と実際に保存されている内容を一致させる。ただし、待っている間に
   * 他の更新(ログアウトによる初期化など)が入っていた場合は、それを上書きしないよう
   * 巻き戻さない。
   *
   * @returns 書き込みの結果({@link GlossarySaveResult})
   */
  const changeGlossaryEntries = useCallback(
    async (value: GlossaryRow[]): Promise<GlossarySaveResult> => {
      const previousEntries = reduxStore.getState().glossaryEntries.entries;
      const failureCountBeforeWrite = getWriteFailureCount();
      const deadline = Date.now() + FLUSH_TIMEOUT_MS;

      // 同じ配列(参照)をそのままdispatchすると、失敗後の再登録などで「変化なし」と
      // 扱われてredux-persistが書き込みを行わず、保存できていないのに成功と判定
      // されてしまう。必ず書き込みが試みられるよう、新しい配列として渡す。
      const dispatchedEntries = [...value];
      dispatch(setGlossaryEntries(dispatchedEntries));

      const timedOut = await flushWithTimeout(FLUSH_TIMEOUT_MS);

      // 巻き戻しの書き込みが失敗回数を変えても結果に影響しないよう、
      // 結果は巻き戻しの前に確定させる
      let result: GlossarySaveResult;
      if (timedOut) {
        result = GLOSSARY_SAVE_RESULT.TIMEOUT;
      } else if (getWriteFailureCount() === failureCountBeforeWrite) {
        result = GLOSSARY_SAVE_RESULT.SUCCEEDED;
      } else {
        result =
          getLastWriteFailureErrorName() === 'QuotaExceededError'
            ? GLOSSARY_SAVE_RESULT.FAILED_QUOTA
            : GLOSSARY_SAVE_RESULT.FAILED_BLOCKED;
      }

      // 待っている間にログアウトによる初期化などで用語集が更新されていた場合は、
      // その更新を古い内容で上書き(ログアウト後に用語集を再保存)してしまわないよう、
      // 今回dispatchした内容のままのときだけ巻き戻す
      const isUnchangedSinceDispatch =
        reduxStore.getState().glossaryEntries.entries === dispatchedEntries;

      if (
        result !== GLOSSARY_SAVE_RESULT.SUCCEEDED &&
        isUnchangedSinceDispatch
      ) {
        dispatch(setGlossaryEntries(previousEntries));
        // 巻き戻した状態を保存側にも反映する(この結果は無視する)。
        // 呼び出し元を待たせる時間が上限(FLUSH_TIMEOUT_MS)を超えないよう、
        // 登録時と同じ期限の残り時間だけ待つ
        await flushWithTimeout(Math.max(0, deadline - Date.now()));
      }

      return result;
    },
    [dispatch, reduxStore, setGlossaryEntries],
  );

  /**
   * 用語集に関する全てのStateをリセット
   */
  const resetState = useCallback(() => {
    dispatch(clearGlossaryEntries());
  }, [clearGlossaryEntries, dispatch]);

  return {
    entries,
    setGlossaryEntries: changeGlossaryEntries,
    resetState,
  };
};
